import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";
import type { IPreset } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { fetchService } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import { getAPIPrefixForRT } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { EKeywordsReportTableFilters } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/tablePresets/KeywordsReportTableFiltersConsts";
import { getApiParams } from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/shared/KeywordsReportUtils";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { RankTrackerQueryType } from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery, useRTApiDuration } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";
import { retryOnAbort } from "app/UtilitiesAndConstants/UtilityFunctions/query";

const presetsBoxesConfig: IPreset<EKeywordsReportTableFilters>[] = [
    {
        get title() {
            return translate("rankTracker.keywords.presets.trafficDrivers");
        },
        get tooltipContent() {
            return translate("rankTracker.keywords.presets.trafficDrivers.tooltip");
        },
        id: EKeywordsReportTableFilters.TRAFFIC_DRIVERS,
        data: undefined,
    },
    {
        get title() {
            return translate("rankTracker.keywords.presets.opportunities");
        },
        get tooltipContent() {
            return translate("rankTracker.keywords.presets.opportunities.tooltip");
        },
        id: EKeywordsReportTableFilters.OPPORTUNITIES,
        data: undefined,
    },
    {
        get title() {
            return translate("rankTracker.keywords.presets.organicCannibalization");
        },
        get tooltipContent() {
            return translate("rankTracker.keywords.presets.organicCannibalization.tooltip");
        },
        id: EKeywordsReportTableFilters.ORGANIC_CANNIBALIZATION,
        data: undefined,
    },
    {
        get title() {
            return translate("rankTracker.keywords.presets.paidCanibalization");
        },
        get tooltipContent() {
            return translate("rankTracker.keywords.presets.paidCanibalization.tooltip");
        },
        id: EKeywordsReportTableFilters.PAID_CANNIBALIZATION,
        data: undefined,
    },
    {
        get title() {
            return translate("rankTracker.keywords.presets.targetUrlMissRank");
        },
        get tooltipContent() {
            return translate("rankTracker.keywords.presets.targetUrlMissRank.tooltip");
        },
        id: EKeywordsReportTableFilters.TARGET_URL_MIS_RANK,
        data: undefined,
    },
    // in comment due we want have this filter in compare mode
    // {
    //     title: translate("rankTracker.keywords.presets.dropped"),
    //     tooltipContent: translate("rankTracker.keywords.presets.dropped.tooltip"),
    //     id: EKeywordsReportTableFilters.DROPPED,
    //     data: undefined,
    // },
    // {
    //     title: translate("rankTracker.keywords.presets.winning"),
    //     tooltipContent: translate("rankTracker.keywords.presets.winning.tooltip"),
    //     id: EKeywordsReportTableFilters.COMPETITOR_WINNING,
    //     data: undefined,
    // },
];

type KeywordsQuickFiltersData = {
    targetUrlMisRank: number;
    opportunities: number;
    dropped: number;
    organicCannibalization: number;
    paidCannibalization: number;
    competitorWinning: number;
    trafficDrivers: number;
};

type KeywordsQuickFiltersQueryParams = {
    // We pass it exclusivity (not via context) since this hook is called in Dashboards
    currentCampaignConfiguration: IRTCurrentCampaignData;
};

export const useKeywordsQuickFiltersQuery = ({ currentCampaignConfiguration }: KeywordsQuickFiltersQueryParams) => {
    const { params } = useCurrentStateAndParams<RTRawParams>();
    const { asc, sortedColumn, ...filterParams } = params;

    const durationObject = useRTApiDuration(currentCampaignConfiguration);

    const { data, ...rest } = useRankTrackerQuery({
        queryKey: createCampaignQueryKey(currentCampaignConfiguration?.id, RankTrackerQueryType.KeywordsQuickFilters, [
            currentCampaignConfiguration?.keywordCount,
            filterParams,
        ]),
        enabled:
            !!currentCampaignConfiguration && currentCampaignConfiguration.id === params.campaignId && !!durationObject,
        queryFn: async ({ signal: cancellation }) => {
            if (!currentCampaignConfiguration || !durationObject) {
                return undefined;
            }
            const sitesParam = [
                currentCampaignConfiguration.site,
                ...currentCampaignConfiguration.competitors.map((competitor) => competitor.site),
            ]
                .filter((site) => site)
                .join(",");

            const apiParams = getApiParams(
                durationObject,
                currentCampaignConfiguration,
                params,
                params.graphGranularity,
                sitesParam,
            );

            return fetchService.get<KeywordsQuickFiltersData>(
                `${getAPIPrefixForRT(!!currentCampaignConfiguration.isDemo)}/reports/keywordsReport/UseCaseFilters`,
                apiParams,
                { cancellation },
            );
        },
        retry: retryOnAbort,
        throwOnError: (error) => {
            Sentry.error(`Error fetching table's filters for keywords report`, error);
            return false;
        },
    });

    const filtersData = useMemo(() => {
        if (!data) {
            return presetsBoxesConfig;
        }
        return presetsBoxesConfig.map((item) => {
            return {
                ...item,
                data: data[item.id],
            };
        });
    }, [data]);

    return {
        data: filtersData,
        ...rest,
    };
};
